import React, { Fragment, useRef, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { Listbox, Transition } from '@headlessui/react';
import { ChevronDown, Check } from 'lucide-react';
import './TableDropdown.css';

const DropdownPortal = ({ children, buttonRef }) => {
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });

  useEffect(() => {
    if (buttonRef.current) {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      const updatePosition = () => {
        const rect = buttonRef.current.getBoundingClientRect();
        // Use fixed positioning relative to viewport
        const viewportHeight = window.innerHeight;
        const spaceBelow = viewportHeight - rect.bottom;
        const dropdownHeight = 240; // Approximate max height from CSS
        
        // Determine if we should show dropdown above or below
        const showAbove = spaceBelow < dropdownHeight && rect.top > dropdownHeight;
        
        setPosition({
          top: showAbove ? rect.top - dropdownHeight : rect.bottom,
          left: rect.left,
          width: rect.width,
          showAbove
        });
      };

      updatePosition();
      
      // Update position on scroll or resize
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);
      
      return () => {
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [buttonRef]);

  return ReactDOM.createPortal(
    <div style={{
      position: 'fixed',
      top: `${position.top}px`,
      left: `${position.left}px`,
      minWidth: `${position.width}px`,
      zIndex: 99999, // Increased z-index to match CSS
      pointerEvents: 'auto' // Ensure dropdown is clickable
    }}>
      {children}
    </div>,
    document.body
  );
};

const TableDropdown = ({
  value,
  options = [],
  onChange,
  placeholder = 'Select...',
  disabled = false,
  className = '',
  'aria-label': ariaLabel
}) => {
  const buttonRef = useRef(null);
  
  // Local state for immediate UI feedback
  const [localValue, setLocalValue] = useState(value);
  
  // Sync local value with prop value when it changes
  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  // Convert options to consistent format
  const normalizedOptions = options.map(opt => {
    if (typeof opt === 'string') {
      return { value: opt, label: opt };
    }
    return opt;
  });

  // Get selected option - use local value for immediate feedback
  const selectedOption = normalizedOptions.find(opt => opt.value === localValue);
  
  // Handle selection with immediate local update
  const handleChange = (newValue) => {
    // Update local state immediately for instant UI feedback
    setLocalValue(newValue);
    // Call the parent's onChange handler
    onChange(newValue);
  };

  // Debug logging for Ready to Invoice dropdown
  if ((className && className.includes('ready-invoice')) || (ariaLabel && ariaLabel.includes('invoice'))) {
    console.log('Ready to Invoice dropdown debug:', {
      propValue: value,
      localValue,
      options: normalizedOptions,
      selectedOption,
      disabled,
      className,
      ariaLabel
    });
  }

  return (
    <Listbox value={localValue} onChange={handleChange} disabled={disabled}>
      {({ open }) => (
        <div className={`table-dropdown ${className} ${open ? 'open' : ''} ${disabled ? 'disabled' : ''}`}>
          <Listbox.Button 
            ref={buttonRef}
            className="table-dropdown-button"
            aria-label={ariaLabel || 'Select option'}
            data-placeholder={!selectedOption}
          >
            <span className="table-dropdown-value">
              {selectedOption ? selectedOption.label : placeholder}
            </span>
            <ChevronDown 
              className={`table-dropdown-icon ${open ? 'rotate' : ''}`}
              size={14}
            />
          </Listbox.Button>

          {open && (
            <DropdownPortal buttonRef={buttonRef}>
              <Transition
                as={Fragment}
                show={open}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <Listbox.Options 
                  className="table-dropdown-options table-dropdown-portal"
                  static
                >
                  {normalizedOptions.length === 0 ? (
                    <div className="table-dropdown-empty">
                      No options available
                    </div>
                  ) : (
                    normalizedOptions.map((option) => (
                      <Listbox.Option
                        key={option.value}
                        value={option.value}
                        disabled={option.disabled}
                        className={({ active, selected }) =>
                          `table-dropdown-option ${
                            active ? 'active' : ''
                          } ${selected ? 'selected' : ''} ${
                            option.disabled ? 'disabled' : ''
                          }`
                        }
                      >
                        {({ selected, active }) => (
                          <>
                            <span className="table-dropdown-option-text">
                              {option.label}
                            </span>
                            {selected && (
                              <Check 
                                className="table-dropdown-check" 
                                size={16}
                              />
                            )}
                          </>
                        )}
                      </Listbox.Option>
                    ))
                  )}
                </Listbox.Options>
              </Transition>
            </DropdownPortal>
          )}
        </div>
      )}
    </Listbox>
  );
};

export default TableDropdown;